import { useEffect, useRef, useState } from 'react';
import { ClickAwayListener, Popper, TextField } from '@material-ui/core';
import { useStyles } from './CommentPopover.styles';
import { ActionMenuItem } from '../ActionsMenu/ActionsMenu.types';
import ActionsMenu from '../ActionsMenu/ActionsMenu.component';
import { CloseIcon } from '../Solution/Menu/CloseIcon.component';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { CommentTypeId, IComment } from '../Comments/Comments.types';
import { getFunctionLabel } from '../../pages/Assessment/Assessment.types';
import CommentSaveDisabled from '../../assets/icons/commentSaveDisabled.svg';
import CommentSaveEnabled from '../../assets/icons/commentSaveEnabled.svg';
import useSessionUser from '../../hooks/useSessionUser';
import { Prompt } from '../Prompt/Prompt.component';
import { addComment, deleteComment, updateComment } from '../../services/exams/examTaskGroup/examTaskGroup.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../Context/Layout.context';
import { getReferenceObject, PopoverAnchorCoords, PopoverPlacement } from '../../utils/popoverUtils';
import { highContrastMode } from '../../utils/highContrastChecker';

const MAX_COMMMENT_LENGTH = 255;
export const COMMENT_POPPER_WIDTH = 355;

interface CommentPopoverProps {
  orgzId: string;
  comment: IComment;
  anchorCoords: PopoverAnchorCoords | null;
  onClose?: () => void;
  onCancel?: () => void;
  onSave?: (comment: IComment, content: string) => void;
  onRemove?: (comment: IComment) => void;
  mode: 'create' | 'view';
  avoidAwayClosing?: boolean;
  readOnly?: boolean;
  placement?: PopoverPlacement;
}

export const CommentPopover: React.FC<CommentPopoverProps> = (props) => {
  const classes = useStyles();
  const { user } = useSessionUser();
  const { openAlert } = useLayoutContext();
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [newComment, setNewComment] = useState<string>(props.comment.content);
  const [isEdit, setIsEdit] = useState<boolean>(props.mode === 'create');
  const [deleteCommentPromptVisible, setDeleteCommentPromptVisible] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const border = highContrastMode ? "solid 1px black" : 'solid 1px #3C419A';
  const headerBoarder = highContrastMode ? '1px solid rgba(44, 45, 62, 0.3)' : ''
  const background = highContrastMode ? 'black' : 'linear-gradient(to bottom, #FFFFFF 0px, #FFFFFF 34px, #F5F5F8 36px, #F5F5F8 100% )'

  
  const handleClose = () => {
    props.onClose && props.onClose();
    setIsEdit(false);
  };

  const [handleClickAway, setHandleClickAway] = useState<(() => void) | undefined>(() => handleClose);

  const handleEditComment = () => {
    setNewComment(props.comment.content);
    setIsEdit(true);
  };

  const handleRemoveComment = () => {
    deleteComment(props.comment.commentId).then(
      (response: AxiosResponse<boolean>) => {
        if (response.data) {
          setNewComment('');
          setIsEdit(false);
          props.onRemove && props.onRemove(props.comment);
          setDeleteCommentPromptVisible(false);
        } else {
          openAlert('error');
          setDeleteCommentPromptVisible(false);
        }
      },
      () => {
        openAlert('error');
        setDeleteCommentPromptVisible(false);
      },
    );
  };

  const handleCancelEdit = () => {
    setNewComment(props.comment.content);
    setIsEdit(false);
    props.mode === 'create' && props.onCancel && props.onCancel();
  };

  const handleSaveComment = () => {
    if (props.mode === 'create' && props.comment.x !== null && props.comment.y !== null) {
      addComment(
        props.orgzId,
        CommentTypeId.ONIMAGE,
        newComment,
        props.comment.x,
        props.comment.y,
        props.comment.pageNumber,
      ).then(
        (response: AxiosResponse<boolean>) => {
          if (response.data) {
            setIsEdit(false);
            props.onSave && props.onSave(props.comment, newComment);
          } else {
            openAlert('error');
          }
        },
        () => {
          openAlert('error');
        },
      );
    } else if (props.orgzId && props.comment.x !== null && props.comment.y !== null) {
      updateComment(
        props.comment.commentId,
        newComment,
        props.comment.x,
        props.comment.y,
        props.comment.pageNumber,
      ).then(
        (response: AxiosResponse<boolean>) => {
          if (response.data) {
            setIsEdit(false);
            props.onSave && props.onSave(props.comment, newComment);
          } else {
            openAlert('error');
          }
        },
        () => {
          openAlert('error');
        },
      );
    }
  };

  const openDeleteCommentPrompt = () => {
    setDeleteCommentPromptVisible(true);
  };

  const closeDeleteCommentPrompt = () => {
    setDeleteCommentPromptVisible(false);
  };

  const getActionsMenuIcon = (): JSX.Element | null => {
    const currentUserIsAuthor = props.comment.examinerId === user.profile.sub;
    if (!currentUserIsAuthor) {
      return null;
    }
    const menuItems: ActionMenuItem[] = [
      {
        text: 'Edytuj',
        onClick: () => handleEditComment(),
      },
      {
        text: 'Usuń',
        onClick: openDeleteCommentPrompt,
      },
    ];
    return <ActionsMenu menuItems={menuItems} />;
  };

  useEffect(() => {
    if (deleteCommentPromptVisible || isEdit) {
      setHandleClickAway(undefined);
    } else {
      setHandleClickAway(() => handleClose);
    }
  }, [deleteCommentPromptVisible, isEdit]);

  const anchorEl =
    props.anchorCoords !== null ? getReferenceObject(props.anchorCoords, popoverRef, props.placement) : null;
  const open = !!anchorEl;
  const id = open ? 'comment' : undefined;
  const authorWithFunction = `${props.comment.examinerFirstName} ${props.comment.examinerLastName} (${getFunctionLabel(
    props.comment.examinerFunction,
  )})`;

  const tooLongComment = newComment.length > MAX_COMMMENT_LENGTH;
  const isDataValid = newComment.length > 0 && !tooLongComment;

  useEffect(() => {
    setIsDataChanged(newComment !== props.comment.content);
  },[newComment, props.comment.content]);

  return (
    <>
      <div
        style={{ zIndex: 1000 }}
        onKeyDown={(e) => {
          // prevents firing key shortcuts when typing
          e.stopPropagation();
        }}
      >
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement={'right-start'}
          transition
          disablePortal
          style={{ width: `${COMMENT_POPPER_WIDTH}px` }}
        >
          <ClickAwayListener
            onClickAway={() => {
              !props.avoidAwayClosing && handleClickAway && handleClickAway();
            }}
          >
            <div className={classes.popover} style={{border: border, background: background}} ref={popoverRef}>
              <div className={classes.header} style={{border: headerBoarder}}> 
                <div className={classes.title}>{authorWithFunction}</div>
                <div className={classes.icons}>
                  {props.mode === 'view' && !props.readOnly && (
                    <div className={classes.actionIcon}>{getActionsMenuIcon()}</div>
                  )}
                  <div className={classes.closeIcon} onClick={handleClose}>
                    <CloseIcon />
                  </div>
                </div>
              </div>
              {!isEdit ? (
                <div className={classes.comment}>
                  <TextField className={classes.inputReadOnly} variant="standard" value={newComment} multiline />
                </div>
              ) : (
                <div className={classes.newComment}>
                  <TextField
                    className={classes.input}
                    multiline
                    variant="outlined"
                    fullWidth
                    minRows={props.mode === 'view' ? 3 : 1}
                    maxRows={4}
                    defaultValue={props.comment.content}
                    placeholder="Dodaj komentarz..."
                    inputRef={textAreaRef}
                    aria-label="komentarz"
                    onKeyUp={() => {
                      // used onKeyUp not onChange in order to fix Firefox's strange scrolling to top when editing long textaera
                      setNewComment(textAreaRef.current ? textAreaRef.current.value : '');
                    }}
                  />

                  <div className={classes.signsCounter} style={tooLongComment ? { color: '#BC0000' } : undefined}>
                    {newComment.length ?? 0}/{MAX_COMMMENT_LENGTH}
                    {tooLongComment && <div>Komentarz jest za długi</div>}
                  </div>
                  {props.mode === 'create' ? (
                    <div className={classes.actonButtons}>
                      {isDataValid ? (
                        <img
                          src={CommentSaveEnabled}
                          onClick={handleSaveComment}
                          alt="przycisk zapisujący nowy komentarz"
                        />
                      ) : (
                        <img src={CommentSaveDisabled} alt="brak możliwości zapisania komentarza" />
                      )}
                    </div>
                  ) : (
                    <div className={classes.actonButtons}>
                      <ButtonOne
                        color="primary"
                        variant="outlined"
                        onClick={handleCancelEdit}
                        style={{ margin: '5px' }}
                      >
                        Anuluj
                      </ButtonOne>
                      <ButtonOne
                        color="primary"
                        variant="contained"
                        onClick={handleSaveComment}
                        style={{ margin: '5px' }}
                        disabled={!isDataValid || !isDataChanged}
                      >
                        Zapisz
                      </ButtonOne>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ClickAwayListener>
        </Popper>
      </div>
      {deleteCommentPromptVisible && (
        <Prompt
          type="confirmation"
          mainActionButtonText="TAK"
          open={true}
          mainText="Czy na pewno chcesz usunąć komentarz?"
          onConfirm={handleRemoveComment}
          onCancel={closeDeleteCommentPrompt}
        />
      )}
    </>
  );
};
